// Split out of src/components/ChildView.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). ChildView.jsx
// re-exports everything it exported before, so existing imports keep working.

import { coachEnabled } from "../../lib/coachEnabled.js";
import { Icon, OinkIcon, Piggington } from "../Common.jsx";
import { NumericKeypad } from "../Tests.jsx";
import { withAnswerPrompt } from "../../lib/maths.js";
import { PERFECT_WEEK_OP, dailyQuestionFor, dailyQuestionOP, isDoubleOinkDay } from "../../lib/dailyQuestion.js";
import { adaptiveDraw, adaptiveParams, gameState as adaptiveGameState } from "../../lib/adaptive.js";
import { cmsConfig, t as ct } from "../../lib/content.js";
import { effectiveStarterVocabList, effectiveVocabCategories, resolveVocabCategory } from "../../lib/vocab.js";
import { useBackLayer } from "../../lib/backstack.js";
import { todayStr } from "../../lib/utils.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------- Piggington's Daily Question --------------------------- */

// One question a day per child, paying Oinks only — deterministic per (child, day) from
// lib/dailyQuestion.js, so nothing persists until answered and every device shows the same
// question all day. One attempt: the day's result locks on first answer (dailyQ in
// learningContent), and the card flips to its answered state until midnight. Gated by the
// same oinksEnabled toggle as everything Oink.

export function DailyQuestionCard({ child, learning, oinksOn, onAnswer, rewardSettings }) {
  const [overlayOpen, setOverlayOpen] = useState(false);
  useBackLayer(overlayOpen, () => setOverlayOpen(false)); // LB-315: the daily question sheet
  // "justAnswered" distinguishes an answer given THIS visit (show the Got it!/Oinks panel,
  // then dismiss after 3s) from one already answered on an earlier visit today (skip
  // straight to hidden — nothing left to show, no point replaying the reveal). "dismissed"
  // is the terminal state once the 3s window elapses; it's local component state on purpose,
  // so navigating away and back to a fresh mount re-evaluates from `answered` alone and stays
  // hidden rather than flashing the confirmation panel a second time.
  const [justAnswered, setJustAnswered] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const dismissTimerRef = useRef(null);
  useEffect(() => () => { if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current); }, []);

  if (!oinksOn) return null;
  const today = todayStr();
  const dailyQ = learning?.dailyQ;
  const answered = dailyQ && dailyQ.date === today;
  if (dismissed || (answered && !justAnswered)) return null;
  const double = isDoubleOinkDay(child.id, today);
  // Adaptive: hand the DQ an achievable vocab pool drawn at the child's target (results never count).
  const adaptivePool = (() => {
    try {
      if (!coachEnabled(rewardSettings) || !learning) return null;
      const byAge = resolveVocabCategory(child, learning.vocabCategory); if (byAge === "custom") return null;
      const st = adaptiveGameState(learning.adaptive || {}, "vocab", byAge);
      const band = st.band && effectiveVocabCategories()[st.band] ? st.band : byAge;
      const items = effectiveStarterVocabList(band).map((w, i) => ({ ...w, id: `dq:${band}:${i}`, options: [w.definition, ...w.distractors] }));
      return adaptiveDraw(items, st, adaptiveParams(cmsConfig("adaptive.params")), 8);
    } catch { return null; }
  })();
  const question = dailyQuestionFor(child, learning || {}, today, adaptivePool);

  function handleAnswer(result) {
    onAnswer(result);
    setJustAnswered(true);
    dismissTimerRef.current = setTimeout(() => setDismissed(true), 3000);
  }

  return (
    <div data-testid="daily-question-card" className={`kc kc-play rounded-2xl p-3.5 relative ${answered ? "" : "kc-now"}`}>
      {double && !answered && (
        <span data-testid="double-oink-tag" className="inline-flex items-center gap-1 text-xs font-bold text-navy bg-marigold rounded px-2.5 py-0.5 mb-1.5">{ct("kid.double_oink_chip", "×2 Double Oink day")}</span>
      )}
      <div className="font-extrabold kc-title text-base flex items-center gap-2"><span className="kc-disc" aria-hidden="true"><Icon id="ic-brain" size={18} style={{ color: "currentColor" }} /></span>Today's Question</div>
      {answered ? (
        <div>
          <div className={`flex items-center gap-2.5 rounded-xl px-3 py-2 mt-2 border ${dailyQ.result === "correct" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
            <Icon id={dailyQ.result === "correct" ? "ic-celebrate" : "ic-reassure"} size={22} hue={dailyQ.result === "correct" ? "pink" : "indigo"} />
            <div className="min-w-0">
              <div className={`font-bold text-xs ${dailyQ.result === "correct" ? "text-emerald-700" : "text-amber-700"}`}>{dailyQ.result === "correct" ? "Got it" : "Good try — you'll get tomorrow's."}</div>
              <div className="inline-flex items-center gap-1 font-bold text-xs text-pink-600 mt-0.5"><OinkIcon className="w-4 h-4" /> +{dailyQuestionOP(dailyQ.result, double)} Oinks{double ? " (double day!)" : ""}</div>
              {/* Perfect Week (Aug 2026 characters/economy task) — an activity line distinct
                  from the Oinks-earned line above, since it's a separate bonus (App.jsx's
                  onAnswerDailyQuestion adds PERFECT_WEEK_OP on top of the normal payout the
                  same update). Only ever true the day it's actually earned — dailyQ.perfectWeek
                  is set once, on the 7th day, never retroactively on later reads. */}
              {dailyQ.perfectWeek && (
                <div data-testid="perfect-week-line" className="inline-flex items-center gap-1 font-bold text-xs text-amber-600 mt-0.5"><Icon id="ic-trophy" size={14} hue="gold" />{ct("daily_question.perfect_week", "Perfect Week +{op} Oinks", { op: PERFECT_WEEK_OP })}</div>
              )}
            </div>
          </div>
          <div data-testid="daily-question-locked" className="text-xs font-medium text-slate-400 text-center mt-2">{ct("daily_question.new_tomorrow", "New question tomorrow")}</div>
        </div>
      ) : (
        <div>
          <div className="text-xs text-slate-500 font-medium mt-0.5 mb-2.5">{ct("daily_question.meta", "One a day. Answer it to earn")} <b className="text-pink-600">+{dailyQuestionOP("correct", double)} Oinks</b></div>
          <button data-testid="daily-question-answer" className="bg-slate-900 text-white font-bold text-xs rounded-xl px-4 py-2" onClick={() => setOverlayOpen(true)}>{ct("daily_question.answer_button", "Answer it")}</button>
        </div>
      )}
      {overlayOpen && (
        // Deliberately NOT gated on `!answered` here: answering is exactly what flips
        // `answered` true (via onAnswer -> App's state update), so gating on it would
        // unmount this overlay the instant a result is recorded — before the child ever
        // sees "Got it!" or the Oink amount. The "Answer it!" button is already unreachable
        // once answered (replaced by the locked-state markup below), so overlayOpen alone
        // is a sufficient and correct guard against re-opening on an already-answered day.
        <DailyQuestionOverlay question={question} double={double} onAnswer={(result) => { handleAnswer(result); }} onClose={() => setOverlayOpen(false)} />
      )}
    </div>
  );
}

function DailyQuestionOverlay({ question, double, onAnswer, onClose }) {
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null); // null | "correct" | "wrong"
  const submittedRef = useRef(false); // one attempt, enforced locally too (the App handler also guards)

  function submit(chosen) {
    if (submittedRef.current) return;
    let correct;
    if (question.kind === "vocab") {
      correct = chosen === question.definition;
    } else {
      if (input.trim() === "") return;
      correct = Number(input) === question.answer;
    }
    submittedRef.current = true;
    const r = correct ? "correct" : "wrong";
    setResult(r);
    onAnswer(r);
  }

  return (
    <div data-testid="daily-question-overlay" className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-5 relative">
        {/* Device pass (Alan, 8 Sep): there was no way out of an unanswered question except
            answering it. Close keeps the question for later; nothing is recorded. */}
        {result === null && (
          <button onClick={onClose} aria-label="Close" title="Close" className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-pill text-navy flex items-center justify-center"><Icon id="ic-close" size={14} style={{ color: "currentColor" }} /></button>
        )}
        {double && <span className="inline-flex items-center gap-1 text-xs font-bold text-navy bg-marigold rounded px-2.5 py-0.5">{ct("kid.double_oink_chip", "×2 Double Oink day")}</span>}
        {result === null && (
          <div className="flex justify-center -mt-1">
            <Piggington expression="fx-thinking" className="w-14 h-[73px]" />
          </div>
        )}
        {result === null ? (
          question.kind === "vocab" ? (
            <div>
              <div className="text-center font-extrabold text-2xl text-slate-900 my-3">{question.word}</div>
              <div className="text-center text-xs text-slate-500 font-medium mb-3">{ct("daily_question.what_does_it_mean", "What does it mean?")}</div>
              <div className="flex flex-col gap-2">
                {question.options.map((opt, i) => (
                  <button key={i} className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-700 text-left" onClick={() => submit(opt)}>{opt}</button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div data-testid="daily-question-prompt" className="text-center font-extrabold text-2xl text-slate-900 my-3">{withAnswerPrompt(question.prompt)}</div>
              <NumericKeypad value={input} disabled={false} allowDecimal allowNegative onEnter={() => submit()} onDigit={(d) => setInput((v) => (v.length < 6 ? v + d : v))} onBackspace={() => setInput((v) => v.slice(0, -1))} onDecimal={() => setInput((v) => (v.includes(".") || v.length >= 6 ? v : v + "."))}
                onToggleSign={() => setInput((v) => (v.startsWith("-") ? v.slice(1) : `-${v}`))} />
              <button className="w-full bg-slate-900 text-white font-bold rounded-xl py-2.5 text-sm mt-3" onClick={() => submit()}>{ct("daily_question.check_button", "Check")}</button>
              <div className="text-xs font-medium text-slate-400 text-center mt-2">One shot — make it count.</div>
            </div>
          )
        ) : (
          <div className="text-center py-2">
            <div className="mb-2 flex justify-center"><Icon id={result === "correct" ? "ic-celebrate" : "ic-reassure"} size={44} hue={result === "correct" ? "pink" : "indigo"} /></div>
            <div className="font-extrabold text-lg text-slate-900 mb-1">{result === "correct" ? "Got it" : "Not quite"}</div>
            {result === "wrong" && (
              <div className="text-sm text-slate-500 mb-2">{question.kind === "vocab" ? <span><b>{question.word}</b> means: {question.definition}</span> : <span>{question.prompt.trim().endsWith("?") ? <>{question.prompt} <b>{question.answer}</b></> : <>{question.prompt} = <b>{question.answer}</b></>}</span>}</div>
            )}
            <div className="inline-flex items-center gap-1.5 font-bold text-sm text-pink-600 mb-3"><OinkIcon className="w-5 h-5" /> +{dailyQuestionOP(result, double)} Oinks{double ? " (double day!)" : ""}</div>
            <button className="w-full bg-slate-900 text-white font-bold rounded-xl py-2.5 text-sm" onClick={onClose}>{ct("daily_question.done_button", "Done")}</button>
          </div>
        )}
      </div>
    </div>
  );
}
